// ============================================================
// Analytics Tracker — Tracking Guards & Admin Exclusion
// ============================================================

import { isTrackingBlocked, markInternalDevice } from "@/lib/gtm";
import { hasAnalyticsConsent } from "@/lib/consent";
import { getAuthUser } from "@/lib/authUser";

// ── Dev Environment Guard ─────────────────────────────────

// Admin emails that should NOT be tracked
const ADMIN_EMAILS = [
  "dimitris@eyecaptain.io",
  "info@eyecaptain.io",
];

let _cachedIsAdmin: boolean | null = null;
let _adminCheckInProgress: Promise<boolean> | null = null;

/**
 * Check if the current user is an admin (by email).
 * Cached after first check per page load. Non-blocking with timeout.
 */
export async function checkIsAdmin(): Promise<boolean> {
  if (_cachedIsAdmin !== null) return _cachedIsAdmin;
  
  // Prevent multiple parallel checks
  if (_adminCheckInProgress) return _adminCheckInProgress;
  
  _adminCheckInProgress = (async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000));
      const userPromise = getAuthUser().then(r => r.data);
      const data = await Promise.race([userPromise, timeoutPromise]);
      if (data?.user?.email && ADMIN_EMAILS.includes(data.user.email.toLowerCase())) {
        // Keep this browser out of GA as well, now and on later visits.
        markInternalDevice();
        _cachedIsAdmin = true;
        return true;
      }
    } catch { /* ignore */ }
    _cachedIsAdmin = false;
    return false;
  })();
  
  const result = await _adminCheckInProgress;
  _adminCheckInProgress = null;
  return result;
}

/**
 * Reset admin cache on auth state change (login/logout).
 */
export function resetAdminCache(): void {
  _cachedIsAdmin = null;
  _adminCheckInProgress = null;
}

export function shouldTrack(): boolean {
  if (typeof window === "undefined") return false;

  const hostname = window.location.hostname;
  const isDev = import.meta.env.DEV;

  if (isDev) return false;
  // Same verdict as the GTM gate in index.html (bot, wrong host, internal device).
  if (isTrackingBlocked()) return false;
  if (hostname.includes("localhost")) return false;
  if (hostname.includes("127.0.0.1")) return false;
  if (hostname.includes("lovable")) return false;

  return true;
}

/**
 * Async version of shouldTrack that also checks admin status and consent.
 * Use this in tracking calls.
 */
export async function shouldTrackAsync(): Promise<boolean> {
  if (!shouldTrack()) return false;
  if (!hasAnalyticsConsent()) return false;
  const isAdmin = await checkIsAdmin();
  return !isAdmin;
}
